import {
  ArrowBackIosNewOutlined,
  ArrowForwardIosOutlined,
} from "@mui/icons-material";
import { Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AllergyAlertCard from "screens/patientInfo/components/sidebarComponents/AllergyAlertCard";
import DocumentReferenceCard from "screens/patientInfo/components/sidebarComponents/DocumentReferenceCard";
import MedicalConditionCard from "screens/patientInfo/components/sidebarComponents/MedicalConditionCard";
import MedicationAccordion from "screens/patientInfo/components/sidebarComponents/MedicationAccordion";
import ProcedureCard from "screens/patientInfo/components/sidebarComponents/ProcedureCard";
import VitalsDisplay from "screens/patientInfo/components/sidebarComponents/VitalDisplay";

// Main component
const EhrPatientDataSidebar = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Mobile
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg")); // Laptop

  const { i18n } = useTranslation();
  const app = useSelector((state) => state.app);
  const recognizer = useSelector((state) => state.recognizer);
  const isEndLoading = Boolean(recognizer?.endLoading);

  const [isOpen, setIsOpen] = useState(false);
  const [rerender, setRerender] = useState(0);
  // Default to disabled (grey) until we know whether any data exists
  const [disableHistorySidebar, setDisableHistorySidebar] = useState(true);
  const [toggleReady, setToggleReady] = useState(false);

  const isMobileDevice = useMediaQuery("(max-width:767px)");
  const { isSidebarVisible, isRecording, isPaused, encounterObj } = useSelector(
    (state) => ({
      isSidebarVisible: state?.quickAccessDashboard?.sidebar?.isVisible,
      isRecording: state?.recognizer?.recording,
      isPaused: state?.recognizer?.paused,
      encounterObj: state?.encounterObj,
    })
  );

  const isSOAPHistoryVisible =
    isSidebarVisible &&
    !isMobileDevice &&
    (isRecording || isPaused || encounterObj?.encounter?.id);

  const toggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isOpen) {
      setRerender((prev) => prev + 1);
    }
  }, [isOpen]);

  // Responsive values based on screen size
  const getSidebarWidth = () => {
    if (isMobile) return "90vw";
    if (isTablet) return "70vw";
    if (isLaptop) return "50vw";
    return "40vw"; // Desktop default
  };

  const isDarkTheme = app?.theme === "dark";
  const muiTheme = theme;
  const { radius, boxShadow } = styles.shared;

  return (
    <>
      {/* Overlay when sidebar is open */}
      {isOpen && <div onClick={toggleSidebar} style={styles.overlay} />}

      {/* Main Sidebar */}
      <div
        style={styles.sidebar(
          isDarkTheme,
          isOpen,
          getSidebarWidth,
          isMobile,
          radius,
          boxShadow
        )}
      >
        <div style={styles.header(isMobile)}>
          <button style={styles.closeButton(isMobile)} onClick={toggleSidebar}>
            <ArrowForwardIosOutlined
              fontSize={isMobile ? "small" : "medium"}
              style={{ alignSelf: "center" }}
            />
          </button>
          <div style={styles.buttonText}>
            <Typography style={styles.sidebarText(isMobile)}>
              EHR Patient Data
            </Typography>
          </div>
        </div>
        <EhrPatientSideBarContent
          setDisableHistorySidebar={setDisableHistorySidebar}
          setToggleReady={setToggleReady}
          scrollRender={rerender}
          isMobile={isMobile}
        />
      </div>

      {/* Toggle Button */}
      {toggleReady && !isOpen ? (
        <Tooltip
          title={disableHistorySidebar ? "No patient data available" : ""}
          placement="left"
          arrow
        >
          <span
            style={{
              position: "fixed",
              top: !isSOAPHistoryVisible ? "40%" : "45%",
              right: 0,
              zIndex: 1200,
            }}
          >
            <div
              onClick={
                !isEndLoading && !disableHistorySidebar
                  ? toggleSidebar
                  : undefined
              }
              role="button"
              aria-disabled={isEndLoading || disableHistorySidebar}
              tabIndex={isEndLoading || disableHistorySidebar ? -1 : 0}
              style={{
                ...styles.toggleButton(
                  disableHistorySidebar,
                  isDarkTheme,
                  radius,
                  boxShadow,
                  isMobile,
                  muiTheme
                ),
                opacity:
                  isEndLoading || disableHistorySidebar ? 0.6 : undefined,
                cursor:
                  isEndLoading || disableHistorySidebar ? "default" : "pointer",
                pointerEvents:
                  isEndLoading || disableHistorySidebar ? "none" : undefined,
                position: "relative",
                top: "auto",
                right: "auto",
              }}
              className="overlay-wrapper"
            >
              <button
                style={styles.toggleIcon(
                  isMobile,
                  isDarkTheme,
                  disableHistorySidebar,
                  muiTheme
                )}
                disabled={isEndLoading || disableHistorySidebar}
                aria-hidden={isEndLoading || disableHistorySidebar}
              >
                <ArrowBackIosNewOutlined
                  fontSize={isMobile ? "small" : "medium"}
                />
              </button>

              <div style={styles.toggleTextWrapper}>
                <Typography
                  sx={styles.toggleText(
                    isMobile,
                    isDarkTheme,
                    disableHistorySidebar,
                    muiTheme
                  )}
                >
                  {i18n.t("SIDEBAR.BUTTON_TEXT")}
                </Typography>
              </div>
            </div>
          </span>
        </Tooltip>
      ) : null}
    </>
  );
};

// Content component
const EhrPatientSideBarContent = ({
  isMobile,
  scrollRender,
  setDisableHistorySidebar,
  setToggleReady,
}) => {
  const {
    app,
    encounterObj: { consolidatedNote },
  } = useSelector((state) => state);
  const isDarkTheme = app?.theme === "dark";
  const patientData = consolidatedNote?.section?.map((item) => {
    return { ...item, parsedResources: JSON.parse(item.title) };
  });

  // Extract data categories
  const allergies = findByCodeSystem(patientData, "allergies", "ehr-allergies");
  const medications = findByCodeSystem(
    patientData,
    "medications",
    "ehr-medications"
  );
  const vitalSigns = findByCodeSystem(patientData, "vitals", "ehr-vitals");
  const conditions = findByCodeSystem(
    patientData,
    "conditions",
    "ehr-conditions-diagnosis"
  );
  const procedures = findByCodeSystem(
    patientData,
    "procedures",
    "ehr-procedures"
  );
  const documentReferences = findByCodeSystem(
    patientData,
    "document-references",
    "ehr-document-references"
  );

  // Determine if any resource has data; if none, inform parent to disable/grey out the toggle
  const hasAnyData = Boolean(
    (conditions && conditions.length) ||
      (medications && medications.length) ||
      (allergies && allergies.length) ||
      (vitalSigns && vitalSigns.length) ||
      (procedures && procedures.length) ||
      (documentReferences && documentReferences.length)
  );

  useEffect(() => {
    if (typeof setDisableHistorySidebar === "function") {
      setDisableHistorySidebar(!hasAnyData);
    }
    if (typeof setToggleReady === "function") {
      // Indicate we have evaluated data presence (true or false)
      setToggleReady(true);
    }
  }, [hasAnyData, setDisableHistorySidebar, setToggleReady]);

  return (
    <div style={styles.contentContainer(isMobile, isDarkTheme)}>
      {conditions?.length ? (
        <section style={styles.section(isMobile)}>
          <h5 style={styles.heading(isMobile, isDarkTheme)}>Conditions</h5>
          <div style={styles.itemsContainer}>
            {conditions.map((condition, index) => {
              const conditionProps = {
                diagnosis: condition?.conditionName || undefined,
                verification: condition?.verificationStatus || undefined,
                status: condition?.clinicalStatus || undefined,
                onsetDate: condition?.onsetDate || undefined,
                severity: condition?.severity || undefined,
                category: condition?.categories?.join(", ") || undefined,
                icd10Code: condition?.icd10Code || undefined,
                notes: condition?.notes || [],
                isDarkTheme: isDarkTheme,
              };
              return <MedicalConditionCard key={index} {...conditionProps} />;
            })}
          </div>
        </section>
      ) : null}

      {medications?.length ? (
        <section style={styles.section(isMobile)}>
          <h5 style={styles.heading(isMobile, isDarkTheme)}>Medications</h5>
          <div style={styles.itemsContainer}>
            {medications.map((medication, index) => {
              const dosage =
                `${medication?.dosageInstructions?.doseQuantity || ""}${
                  medication?.dosageInstructions?.doseUnit || ""
                }` || undefined;
              const quantity =
                `${medication?.supply?.quantity || ""}${
                  medication?.supply?.unit || ""
                }` || undefined;
              const duration =
                `${medication?.supply?.expectedDuration?.value || ""}${
                  medication?.supply?.expectedDuration?.unit || ""
                }` || undefined;
              const medicationItem = {
                prescribed: medication?.authoredOn,
                dosageInstructions: medication?.dosageInstructions?.text,
                dosage,
                medicationName: medication?.medicationName,
                prescriber: medication?.prescriber,
                status: medication?.status,
                asNeeded: medication?.dosageInstructions?.asNeeded,
                therapyType: medication?.therapyType,
                frequency: medication?.dosageInstructions?.frequency,
                route: medication?.dosageInstructions?.route,
                duration,
                quantity,
                refills: medication?.supply?.refills,
                isMobile: isMobile,
                isDarkTheme: isDarkTheme,
              };
              return <MedicationAccordion key={index} {...medicationItem} />;
            })}
          </div>
        </section>
      ) : null}

      {allergies?.length ? (
        <section style={styles.section(isMobile)}>
          <h5 style={styles.heading(isMobile, isDarkTheme)}>Allergies</h5>
          <div style={styles.itemsContainer}>
            {allergies.map((item, index) => {
              const reactionString =
                item?.reactions
                  ?.map((reaction) => reaction?.manifestations?.join(", "))
                  ?.join(", ") || "";

              const reactionCodes =
                item?.reactions
                  ?.map((reaction) => reaction?.codes?.join(", "))
                  ?.join(", ") || "";

              const allergyProps = {
                allergyName: item.allergyName,
                reaction: reactionString,
                verification: item.verificationStatus,
                onsetDate: item.onsetDate,
                ...item,
                isMobile: isMobile,
                isDarkTheme: isDarkTheme,
                category: item.category,
                reactionCodes,
              };

              return <AllergyAlertCard key={index} {...allergyProps} />;
            })}
          </div>
        </section>
      ) : null}

      {vitalSigns?.length ? (
        <section style={styles.section(isMobile)}>
          <h5 style={styles.heading(isMobile, isDarkTheme)}>Vitals</h5>
          <div>
            <VitalsDisplay
              vitalsData={vitalSigns}
              isMobile={isMobile}
              isDarkTheme={isDarkTheme}
            />
          </div>
        </section>
      ) : null}

      {procedures?.length ? (
        <section style={styles.section(isMobile)}>
          <h5 style={styles.heading(isMobile, isDarkTheme)}>Procedures</h5>
          <div style={styles.itemsContainer}>
            {procedures.map((procedure, index) => {
              const procedureProps = {
                procedureName: procedure?.procedureName || "Unknown Procedure",
                procedureCode: procedure?.procedureCode || "N/A",
                status: procedure?.status || "unknown",
                performedDate: procedure?.performedDate || null,
                performedPeriod: procedure?.performedPeriod || null,
                performers: procedure?.performers || [],
                bodySites: procedure?.bodySites || [],
                outcome: procedure?.outcome || "N/A",
                reasonCodes: procedure?.reasonCodes || [],
                notes: procedure?.notes || [],
                isDarkTheme: isDarkTheme,
                icd10Codes: procedure?.icd10Codes || [],
                procedureType: procedure?.procedureType || "",
              };
              return <ProcedureCard key={index} {...procedureProps} />;
            })}
          </div>
        </section>
      ) : null}

      {documentReferences?.length ? (
        <section style={styles.section(isMobile)}>
          <h5 style={styles.heading(isMobile, isDarkTheme)}>Clinical Notes</h5>
          <div style={styles.itemsContainer}>
            {documentReferences.map((doc, index) => {
              const documentProps = {
                typeText: doc?.typeText || "Unknown Clinical Note",
                status: doc?.status || "unknown",
                docStatus: doc?.docStatus || "N/A",
                categories: doc?.categories || [],
                date: doc?.date || "",
                description: doc?.description || "",
                decodedData: doc?.attachment?.decodedData || "",
                period: doc?.period || null,
                isDarkTheme: isDarkTheme,
              };
              return <DocumentReferenceCard key={index} {...documentProps} />;
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
};

//Helper Function
const findByCodeSystem = (data, code, system) =>
  data?.find((item) =>
    item?.code?.coding?.some(
      (coding) => coding?.code === code && coding?.system === system
    )
  )?.parsedResources;

// Style objects
const styles = {
  // Shared styles
  shared: {
    radius: "1rem",
    boxShadow: "-2px 4px 4px #5D5FEF33",
  },

  // Overlay styles
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1100,
    transition: "ease-in-out",
  },

  // Sidebar styles
  sidebar: (
    isDarkTheme,
    isOpen,
    getSidebarWidth,
    isMobile,
    radius,
    boxShadow
  ) => ({
    position: "fixed",
    top: "10%",
    right: 0,
    height: "80vh",
    width: getSidebarWidth(),
    borderRadius: radius,
    backgroundColor: isDarkTheme ? "#414079" : "#f9fafc",
    zIndex: 10000,
    boxShadow: boxShadow,
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.3s ease-in-out",
    display: "flex",
    flexDirection: "column",
    padding: isMobile ? "0.75rem" : "1rem",
    gap: isMobile ? "0.75rem" : "1rem",
    overflow: "hidden",
  }),

  // Header styles
  header: (isMobile) => ({
    display: "flex",
    flexDirection: "row",
    gap: isMobile ? "0.75rem" : "1.25rem",
    alignItems: "center",
  }),

  // Button styles
  closeButton: (isMobile) => ({
    width: isMobile ? "1.5rem" : "1.625rem",
    height: isMobile ? "1.5rem" : "1.625rem",
    backgroundColor: "#5D5FEF",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "none",
    color: "#fff",
    padding: 0,
  }),

  buttonText: {
    backgroundColor: "#5D5FEF",
    padding: "2px 6px",
    borderRadius: "14px",
  },

  // Toggle button styles
  toggleButton: (
    disableHistorySidebar,
    isDarkTheme,
    radius,
    boxShadow,
    isMobile,
    muiTheme
  ) => ({
    position: "fixed",
    top: "40%",
    // use the theme's disabled background color when disabled so it matches normal disabled elements
    backgroundColor: disableHistorySidebar
      ? "grey"
      : isDarkTheme
      ? "#414079"
      : "#f0f2f8",
    borderTopLeftRadius: radius,
    borderBottomLeftRadius: radius,
    right: 0,
    zIndex: 1200,
    padding: isMobile ? "0.5rem 0.3rem" : "0.625rem 0.375rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: boxShadow,
    height: "21vh",
    minHeight: "13dvh",
    minWidth: "3vh",
    width: "5vh",
    cursor: disableHistorySidebar ? "not-allowed" : "pointer",
    pointerEvents: disableHistorySidebar ? "none" : "auto",
    opacity: disableHistorySidebar ? 0.7 : 1,
  }),

  toggleIcon: (isMobile, isDarkTheme) => ({
    width: "3vh",
    height: "3dvh",
    backgroundColor: "#A8A9FF",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "none",
    color: isDarkTheme ? "#414079" : "#5D5FEF",
    // marginBottom: "0.5rem",
    padding: 0,
  }),

  toggleTextWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexGrow: 1,
  },

  // Content styles
  contentContainer: (isMobile, isDarkTheme) => ({
    overflow: "auto",
    flex: 1,
    padding: isMobile ? "0.5rem" : "0.75rem",
    color: isDarkTheme ? "#fff" : "#333",
  }),

  section: (isMobile) => ({
    marginBottom: isMobile ? "0.75rem" : "1.25rem",
  }),

  heading: (isMobile, isDarkTheme) => ({
    fontSize: isMobile ? "1rem" : "1.25rem",
    margin: "0.5rem 0",
    color: isDarkTheme ? "#fff" : "#333",
  }),

  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  // Typography styles
  sidebarText: (isMobile) => ({
    fontSize: isMobile ? "0.875rem" : "1rem",
    whiteSpace: "nowrap",
    color: "#fff",
  }),

  toggleText: (isMobile, isDarkTheme) => ({
    transform: "rotate(-90deg)",
    fontWeight: "500",
    fontSize: "1.66dvh",
    textAlign: "center",
    whiteSpace: "nowrap",
    width: "auto",
    maxWidth: isMobile ? "15vh" : "18vh",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: isDarkTheme ? "#fff" : "#414079",
  }),
};

export default EhrPatientDataSidebar;
